import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  getDataAPI,
  postDataAPI,
  deleteDataAPI,
} from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";

/**
 * LMS - Faculty accounts (Council screen).
 *
 * Council creates faculty (a users row, user_type 6) and assigns them one or
 * more courses. Uses the existing admin api wrapper + adminToken, so it rides
 * the same auth as the rest of the panel. No new reducer needed - the screen
 * keeps its own state.
 */
export default function FacultyList() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");

  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(false);

  // Trade -> Sub-Trade -> Course picker for assigning courses (multi-select).
  const [trades, setTrades] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subCourses, setSubCourses] = useState([]);
  const [selTrade, setSelTrade] = useState("");
  const [selSub, setSelSub] = useState("");
  const [courseNames, setCourseNames] = useState({}); // id -> label, for the chips

  // add / edit form
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", course_ids: [] });

  // set-password
  const [pwShow, setPwShow] = useState(false);
  const [pwId, setPwId] = useState(null);
  const [pw, setPw] = useState("");

  const alert = (payload) => dispatch({ type: ALERT, payload });

  const loadFaculty = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getDataAPI("admin/lms/faculty", token()));
      setFaculty(res.data.data || []);
    } catch (e) {
      alert({ error: "Could not load faculty." });
    }
    setLoading(false);
  };

  const loadTrades = async () => {
    try {
      const res = await dispatch(getDataAPI("admin/lms/content/trades", token()));
      setTrades(res.data.data || []);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => {
    loadFaculty();
    loadTrades();
  }, []);

  const resetCascade = () => {
    setSelTrade("");
    setSelSub("");
    setSubs([]);
    setSubCourses([]);
  };

  const pickTrade = (id) => {
    setSelTrade(id);
    setSelSub("");
    setSubCourses([]);
    const t = trades.find((x) => String(x.id) === String(id));
    setSubs(t ? t.subtrade || [] : []);
  };

  const pickSub = async (id) => {
    setSelSub(id);
    setSubCourses([]);
    if (!id) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/content/subtrade-courses/${id}`, token()));
      const list = res.data.data || [];
      setSubCourses(list);
      // remember names so chips can show them even after switching sub-trade
      setCourseNames((m) => {
        const next = { ...m };
        list.forEach((c) => { next[Number(c.id)] = c.short_name || c.name; });
        return next;
      });
    } catch (e) {
      /* non-fatal */
    }
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ name: "", email: "", password: "", course_ids: [] });
    resetCascade();
    setShow(true);
  };

  const openEdit = (f) => {
    setEditId(f.id);
    setForm({
      name: f.name || "",
      email: f.email || "",
      password: "",
      course_ids: (f.course_ids || []).map((x) => Number(x)),
    });
    // seed the chip names from what the list already knows
    setCourseNames((m) => {
      const next = { ...m };
      (f.courses || []).forEach((c) => { next[Number(c.id)] = c.short_name || c.name; });
      return next;
    });
    resetCascade();
    setShow(true);
  };

  const toggleCourse = (id) => {
    const cid = Number(id);
    setForm((p) => ({
      ...p,
      course_ids: p.course_ids.includes(cid)
        ? p.course_ids.filter((x) => x !== cid)
        : [...p.course_ids, cid],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        name: form.name,
        email: form.email,
        course_ids: form.course_ids,
      };
      if (editId) {
        await dispatch(postDataAPI(`admin/lms/faculty/${editId}`, body, token()));
        alert({ success: "Faculty updated." });
      } else {
        body.password = form.password;
        await dispatch(postDataAPI("admin/lms/faculty", body, token()));
        alert({ success: "Faculty created." });
      }
      setShow(false);
      loadFaculty();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save faculty." });
    }
  };

  const openPw = (id) => {
    setPwId(id);
    setPw("");
    setPwShow(true);
  };

  const savePw = async (e) => {
    e.preventDefault();
    try {
      await dispatch(postDataAPI(`admin/lms/faculty/${pwId}/password`, { password: pw }, token()));
      alert({ success: "Password updated." });
      setPwShow(false);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not update password." });
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove faculty "${f.name}"? This cannot be undone.`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/faculty/${f.id}`, token()));
      alert({ success: "Faculty removed." });
      loadFaculty();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not remove faculty." });
    }
  };

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 style={{ margin: 0 }}>Faculty</h4>
        <Button variant="primary" onClick={openAdd}>
          + Add Faculty
        </Button>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover bg-white">
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Assigned Courses</th>
              <th style={{ width: 230 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center">Loading…</td>
              </tr>
            ) : faculty.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">No faculty yet.</td>
              </tr>
            ) : (
              faculty.map((f, i) => (
                <tr key={f.id}>
                  <td>{i + 1}</td>
                  <td>{f.name}</td>
                  <td>{f.email}</td>
                  <td>
                    {(f.courses || []).length === 0 ? (
                      <span className="text-muted">— none —</span>
                    ) : (
                      (f.courses || [])
                        .map((c) => c.short_name || c.name)
                        .join(", ")
                    )}
                  </td>
                  <td>
                    <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openEdit(f)}>
                      Edit
                    </Button>{" "}
                    <Button size="sm" variant="outline-secondary" className="mr-2" onClick={() => openPw(f.id)}>
                      Password
                    </Button>{" "}
                    <Button size="sm" variant="outline-danger" onClick={() => remove(f)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit */}
      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Edit Faculty" : "Add Faculty"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Name</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Email (used as the login user name)</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            {!editId && (
              <div className="form-group">
                <label>Password (min 5 characters)</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={5}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label>Assign Courses (Trade → Sub-Trade → tick courses)</label>
              <div className="row">
                <div className="col-md-6">
                  <select className="form-control mb-2" value={selTrade} onChange={(e) => pickTrade(e.target.value)}>
                    <option value="">— select trade —</option>
                    {trades.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <select className="form-control mb-2" value={selSub} onChange={(e) => pickSub(e.target.value)} disabled={!selTrade}>
                    <option value="">— select sub-trade —</option>
                    {subs.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div
                style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #ddd", borderRadius: 4, padding: 10 }}
              >
                {!selSub ? (
                  <span className="text-muted">Pick a trade and sub-trade to list its courses.</span>
                ) : subCourses.length === 0 ? (
                  <span className="text-muted">No courses under this sub-trade.</span>
                ) : (
                  subCourses.map((c) => (
                    <label key={c.id} style={{ display: "block", marginBottom: 6, fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={form.course_ids.includes(Number(c.id))}
                        onChange={() => toggleCourse(c.id)}
                        style={{ marginRight: 8 }}
                      />
                      {c.name} {c.short_name ? `(${c.short_name})` : ""}
                    </label>
                  ))
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                <small className="text-muted">Selected ({form.course_ids.length}): </small>
                {form.course_ids.length === 0 ? (
                  <small className="text-muted">none</small>
                ) : (
                  form.course_ids.map((id) => (
                    <span key={id} className="badge badge-primary" style={{ marginRight: 6, marginBottom: 4, display: "inline-block" }}>
                      {courseNames[id] || `#${id}`}{" "}
                      <span style={{ cursor: "pointer" }} onClick={() => toggleCourse(id)}>×</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editId ? "Save" : "Create"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Set password */}
      <Modal show={pwShow} onHide={() => setPwShow(false)}>
        <form onSubmit={savePw}>
          <Modal.Header closeButton>
            <Modal.Title>Set Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>New password (min 5 characters)</label>
              <input
                type="text"
                className="form-control"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                minLength={5}
                required
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setPwShow(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
